import React from 'react'
import { Box, Typography, Button, styled, alpha, Chip } from '@mui/material'
import { Star, ArrowForward, AutoAwesome, Check } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

interface UpgradeCardProps {
  title?: string
  description?: string
  feature?: string
  itemCount?: number
  onUpgrade?: () => void
  variant?: 'banner' | 'full' | 'inline'
}

const PURPLE_THEME = {
  primary: '#8b5cf6',
  primaryHover: '#7c3aed',
  primaryLight: '#a78bfa',
  gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
  gradientHover: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)',
  dark: '#1e1b4b',
}

// Banner variant - responsive premium CTA
const BannerCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
  padding: '20px 16px',
  overflow: 'hidden',
  border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxSizing: 'border-box',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 10% 20%, rgba(139, 92, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(236, 72, 153, 0.15) 0%, transparent 50%)',
    pointerEvents: 'none',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
    animation: 'rotate 20s linear infinite',
    pointerEvents: 'none',
  },

  '@keyframes rotate': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },

  '&:hover': {
    borderColor: alpha('#a78bfa', 0.4),
    transform: 'translateY(-1px)',
    boxShadow: `0 12px 40px ${alpha('#8b5cf6', 0.25)}`,
  },

  [theme.breakpoints.down('md')]: {
    padding: '16px 12px',
    borderRadius: '10px',
  },
}))

// Premium Badge
const PremiumBadge = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '11px',
  height: '24px',
  padding: '0 8px',
  '& .MuiChip-icon': {
    color: '#fbbf24',
    fontSize: '14px',
    marginLeft: '4px',
    marginRight: '2px',
  },
  '& .MuiChip-label': {
    padding: '0 4px',
  },
}))

// Premium Button
const PremiumButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '13px',
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 14px rgba(139, 92, 246, 0.4)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'none',
  width: '100%',

  '&:hover': {
    background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(139, 92, 246, 0.5)',
  },

  '&:active': {
    transform: 'translateY(0px)',
  },

  '& .MuiButton-endIcon': {
    marginLeft: '6px',
  },

  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    padding: '8px 16px',
  },
}))

// Feature Item
const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: 'rgba(255, 255, 255, 0.9)',
  fontSize: '12px',
  fontWeight: 500,

  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    color: '#a78bfa',
    flexShrink: 0,
  },

  '& span': {
    lineHeight: 1.4,
  },
}))

// Full variant
const FullCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '16px',
  background: PURPLE_THEME.gradient,
  padding: '32px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `2px solid ${alpha(PURPLE_THEME.primary, 0.3)}`,

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
    pointerEvents: 'none',
  },

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 16px 48px ${alpha(PURPLE_THEME.primary, 0.4)}`,
    background: PURPLE_THEME.gradientHover,
  },
}))

// Inline variant
const InlineCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '12px',
  background: alpha(PURPLE_THEME.primary, 0.1),
  padding: '20px',
  border: `1px solid ${alpha(PURPLE_THEME.primary, 0.2)}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    background: alpha(PURPLE_THEME.primary, 0.15),
    borderColor: PURPLE_THEME.primary,
    transform: 'translateX(4px)',
  },
}))

const ShimmerEffect = styled(Box)({
  position: 'absolute',
  top: 0,
  left: '-100%',
  width: '100%',
  height: '100%',
  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
  animation: 'shimmer 3s infinite',

  '@keyframes shimmer': {
    '0%': {
      left: '-100%',
    },
    '100%': {
      left: '100%',
    },
  },
})

const UpgradeCard: React.FC<UpgradeCardProps> = ({
  title = 'Unlock Premium Assets',
  description = 'Get unlimited access to all premium materials, accents, and prints',
  feature = 'premium assets',
  itemCount,
  onUpgrade,
  variant = 'banner',
}) => {
  const navigate = useNavigate()

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade()
    } else {
      navigate('/billing')
    }
  }

  if (variant === 'banner') {
    return (
      <BannerCard>
        <ShimmerEffect />
        <Box position='relative' zIndex={2}>
          <Box mb={1.5}>
            <Box display='flex' alignItems='center' gap={1} mb={1}>
              <PremiumBadge icon={<AutoAwesome />} label='Premium' size='small' />
              {itemCount && (
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '11px',
                    fontWeight: 500,
                  }}
                >
                  {itemCount}+ locked
                </Typography>
              )}
            </Box>

            <Typography
              sx={{
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                mb: 0.75,
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 1.5,
                mb: 2,
              }}
            >
              {description}
            </Typography>

            <Box display='flex' flexDirection='column' gap={0.75} mb={2}>
              <FeatureItem>
                <Check />
                <span>All premium assets</span>
              </FeatureItem>
              <FeatureItem>
                <Check />
                <span>Unlimited exports</span>
              </FeatureItem>
              <FeatureItem>
                <Check />
                <span>Priority support</span>
              </FeatureItem>
            </Box>

            <PremiumButton onClick={handleUpgrade} endIcon={<ArrowForward />}>
              Upgrade to Premium
            </PremiumButton>
          </Box>
        </Box>
      </BannerCard>
    )
  }

  if (variant === 'inline') {
    return (
      <InlineCard onClick={handleUpgrade}>
        <Box display='flex' alignItems='center' gap={2}>
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: PURPLE_THEME.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Star sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box flex={1}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: PURPLE_THEME.primary,
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: '#6b7280',
              }}
            >
              Unlock {feature}
            </Typography>
          </Box>
          <ArrowForward sx={{ color: PURPLE_THEME.primary, fontSize: 20 }} />
        </Box>
      </InlineCard>
    )
  }

  // Full variant
  return (
    <FullCard onClick={handleUpgrade}>
      <ShimmerEffect />
      <Box textAlign='center' position='relative' zIndex={1}>
        <Box
          sx={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <Star sx={{ color: 'white', fontSize: 28 }} />
        </Box>

        <Typography
          sx={{
            color: 'white',
            fontSize: '20px',
            fontWeight: 700,
            mb: 1,
            textShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: 'rgba(255,255,255,0.95)',
            fontSize: '14px',
            fontWeight: 500,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>

        <PremiumButton onClick={handleUpgrade} fullWidth endIcon={<ArrowForward />}>
          Upgrade Now
        </PremiumButton>
      </Box>
    </FullCard>
  )
}

export default UpgradeCard
